import React from 'react';
import { GraduationCap, ArrowRight, Globe, Code, Brain, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentLanguage } from '../utils/translations';

const AHBEducationSection = () => {
  const navigate = useNavigate();
  const currentLanguage = getCurrentLanguage();

  const content = {
    fr: {
      badge: 'Nouveau Programme',
      title: 'AHB EDUCATION',
      subtitle: 'Programme International de Formation Digitale',
      description: 'Dans le cadre du partenariat stratégique entre Action-Humanitaire Bénin et Million Coders, nous lançons un programme de formation digitale destiné aux jeunes désireux de renforcer leurs compétences numériques et de s\'insérer dans l\'économie digitale mondiale.',
      features: [
        { icon: Code, text: 'Développement Web' },
        { icon: Brain, text: 'Intelligence Artificielle' },
        { icon: Globe, text: 'Programmation' },
        { icon: Award, text: 'Compétences Numériques' }
      ],
      highlight: 'Formations 100% en ligne • Cours en anglais et arabe • Certificats reconnus',
      enrollmentTitle: 'Comment s\'inscrire ?',
      enrollmentSteps: [
        'S\'acquitter des frais de formation (50 €)',
        'Soumettre votre candidature avec motivation',
        'Validation de votre dossier par notre équipe',
        'Réception des accès à la plateforme Million Coders'
      ],
      benefitsTitle: 'Les avantages du programme',
      benefits: [
        'Formation flexible, accessible à tout moment',
        'Possibilité de bourses pour les apprenants les plus performants',
        'Certifications reconnues à l\'international',
        'Accompagnement en partenariat avec Acacia University'
      ],
      ctaButton: 'En savoir plus',
      price: '50 €'
    },
    en: {
      badge: 'New Program',
      title: 'AHB EDUCATION',
      subtitle: 'International Digital Training Program',
      description: 'As part of the strategic partnership between Action-Humanitaire Bénin and Million Coders, we are launching a digital training program for young people wishing to strengthen their digital skills and integrate into the global digital economy.',
      features: [
        { icon: Code, text: 'Web Development' },
        { icon: Brain, text: 'Artificial Intelligence' },
        { icon: Globe, text: 'Programming' },
        { icon: Award, text: 'Digital Skills' }
      ],
      highlight: '100% online training • Courses in English and Arabic • Recognized certificates',
      enrollmentTitle: 'How to enroll?',
      enrollmentSteps: [
        'Pay the training fee (€50)',
        'Submit your application with motivation',
        'Validation of your file by our team',
        'Receive access to the Million Coders platform'
      ],
      benefitsTitle: 'Program benefits',
      benefits: [
        'Flexible training, accessible at any time',
        'Scholarship opportunities for top performers',
        'Internationally recognized certifications',
        'Support in partnership with Acacia University'
      ],
      ctaButton: 'Learn more',
      price: '€50'
    }
  };

  const t = content[currentLanguage === 'en' ? 'en' : 'fr'];

  const handleLearnMore = () => {
    navigate(`/${currentLanguage}/projet/ahb-education`);
  };

  return (
    <section className="py-8 bg-gradient-to-br from-[#089556] via-[#0a7c4a] to-[#089556] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Badge */}
        <div className="flex justify-center mb-4">
          <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-5 py-2.5 rounded-full text-sm font-semibold border border-white/30 shadow-lg">
            <span className="w-2 h-2 bg-[#07D1EB] rounded-full animate-pulse"></span>
            {t.badge}
          </span>
        </div>

        {/* Main Grid - balanced with image */}
        <div className="grid lg:grid-cols-2 gap-8 items-center mb-8">
          {/* Left Column - Content */}
          <div className="text-white space-y-4 text-center">
            {/* Title */}
            <div>
              <div className="flex items-center justify-center gap-3 mb-2">
                <GraduationCap className="h-8 w-8 text-[#07D1EB]" />
                <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
                  {t.title}
                </h2>
              </div>
              <p className="text-base md:text-lg text-white/95 font-semibold">
                {t.subtitle}
              </p>
            </div>

            {/* Description */}
            <p className="text-sm text-white/90 leading-relaxed">
              {t.description}
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-2">
              {t.features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-lg p-2 border border-white/20"
                >
                  <feature.icon className="h-5 w-5 text-[#07D1EB] flex-shrink-0" />
                  <span className="font-medium text-sm">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Highlight */}
            <div className="bg-[#07D1EB]/20 backdrop-blur-sm border border-[#07D1EB]/40 rounded-lg p-2">
              <p className="text-center font-semibold text-white text-sm">
                {t.highlight}
              </p>
            </div>

            {/* CTA Button - centered */}
            <div className="flex justify-center">
              <button
                onClick={handleLearnMore}
                className="flex items-center justify-center gap-2 bg-white text-[#089556] px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                {t.ctaButton}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Right Column - Poster */}
          <div className="relative">
            {/* Price Badge - Top Right */}
            <div className="absolute -top-4 -right-4 z-20 bg-[#07D1EB] text-white px-5 py-2 rounded-full shadow-2xl font-bold text-sm border-4 border-white">
              {t.price}
            </div>

            {/* Poster Container */}
            <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-3 border border-white/20 shadow-2xl">
              <div className="bg-white rounded-xl overflow-hidden shadow-xl">
                <img
                  src={currentLanguage === 'fr' ? '/affiche_fr.png' : '/affiche_en.png'}
                  alt={currentLanguage === 'fr' ? 'Affiche Formation AHB Education' : 'AHB Education Training Poster'}
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            {/* Decorative Blur Elements */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#07D1EB]/20 rounded-full blur-3xl"></div>
            <div className="absolute -top-6 -right-6 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
          </div>
        </div>

        {/* Bottom Section - Enrollment + Benefits in 2 columns */}
        <div className="grid lg:grid-cols-2 gap-6 border-t border-white/20 pt-6">
          {/* Enrollment Steps */}
          <div>
            <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#07D1EB] rounded-full"></span>
              {t.enrollmentTitle}
            </h3>
            <ul className="space-y-2">
              {t.enrollmentSteps.map((step, index) => (
                <li key={index} className="flex items-start gap-2 text-white/90 text-sm">
                  <span className="flex-shrink-0 w-5 h-5 bg-[#07D1EB] text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                    {index + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Program Benefits */}
          <div>
            <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#07D1EB] rounded-full"></span>
              {t.benefitsTitle}
            </h3>
            <ul className="space-y-2">
              {t.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2 text-white/90 text-sm">
                  <span className="text-[#07D1EB] flex-shrink-0 mt-0.5 text-sm font-bold">✓</span>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AHBEducationSection;
